import React from 'react';
import { PolymorphicWithoutRef } from '../polymorphic';
import { useDniProvider } from '../../../contexts/dni-provider';
import { useGoal } from '../../../contexts/events-provider';

/*
* Memory-only persistence for Invoca/other provider
* phone number replacements, to reduce pop-in as the
* user browses the site.
* */

class PhoneNumberStore {
    numbers: Record<string, string> = {};
    set(originalNumber: string, replacedNumber: string): void {
        if (originalNumber !== replacedNumber) {
            this.numbers[originalNumber] = replacedNumber;
        }
    }
}

const phoneNumberCache = new PhoneNumberStore();

const PhoneNumber = React.forwardRef(<C extends React.ElementType = 'a'>(
    { children, as, ref: _, onClick, ...rest }: PolymorphicWithoutRef<C, {
        children?: string
    }>,
    ref: React.ForwardedRef<HTMLAnchorElement>,
): JSX.Element | null => {
    const Component = as || 'a';

    const [refresh, setRefresh] = React.useState<number>(0);

    const {
        numberMap,
    } = useDniProvider();

    const numberToUse = (() => {
        if (children) {
            if (numberMap[children]) {
                return numberMap[children];
            }
            if (phoneNumberCache.numbers[children]) {
                return phoneNumberCache.numbers[children];
            }
            return children;
        }
        return null;
    })();

    const findReplacedNumber = React.useCallback((currentElement: HTMLAnchorElement | null): void => {
        if (currentElement && typeof currentElement === 'object' && children) {
            if ('innerText' in currentElement) {
                const { innerText } = currentElement;
                phoneNumberCache.set(
                    children,
                    innerText as string,
                );
            }
        }
    }, [children]);

    const track = useGoal();

    const onClickHandler = (e: React.MouseEvent<HTMLElement>): void => {
        if (typeof children === 'string' && children) {
            track('phone_number_clicked', {
                phoneNumber: children, // Use the un-replaced children
            });
        }
        onClick?.(e);
    };

    React.useEffect(
        // eslint-disable-next-line arrow-body-style
        () => {
            /*
            * Start polling for number change if no update
            * has occurred
            * */
            if (children && !phoneNumberCache.numbers[children]) {
                setRefresh(4);
            }
            /*
            * Grab the correct number on unmount
            * */
            const preservedRef = ref as React.RefObject<HTMLAnchorElement>;
            return () => {
                findReplacedNumber(preservedRef?.current || null);
            };
        },
        [children, findReplacedNumber, ref],
    );

    React.useEffect(() => {
        let currentTimer: string | number | NodeJS.Timeout | undefined;
        if (refresh > 0) {
            currentTimer = setTimeout(() => {
                const currentRef = ref as React.RefObject<HTMLAnchorElement>;
                findReplacedNumber(currentRef?.current || null);
                setRefresh(e => e - 1);
            }, 500);
        }
        return () => {
            clearTimeout(currentTimer);
        };
    }, [findReplacedNumber, refresh, ref]);

    if (!numberToUse) {
        return null;
    }

    return (
        <Component
            ref={ref}
            {...rest}
            onClick={onClickHandler}
            to={`tel:${numberToUse}`}
            href={`tel:${numberToUse}`}
        >
            {numberToUse || children}
        </Component>
    );
});

PhoneNumber.displayName = 'PhoneNumber';

export default PhoneNumber;
